/**
 * @module indexedDBService
 * @description IndexedDB Service for Preparo - Persistent storage layer
 *
 * Replaces localStorage with a more robust solution offering:
 * - Larger storage (no 5MB limit)
 * - ACID transactions
 * - Non-blocking asynchronous operations
 * - Support for complex types (Blob, ArrayBuffer)
 * - Indexes for performant queries
 *
 * @example
 * import { db } from './indexedDBService';
 *
 * await db.init();
 * const project = await db.projects.add({ title: 'My Novel' });
 * const docs = await db.documents.getByProject(project.id);
 */

import type {
  Project,
  Document,
  Image,
  ImageSection,
  Template,
  TemplateSection,
  Preset,
  DocumentStatus,
  ExportData,
  DatabaseStats,
  CreateProjectInput,
  CreateDocumentInput,
  UpdateProjectInput,
  UpdateDocumentInput,
  CreateImageInput,
  UpdateImageInput,
  CreateTemplateInput,
  UpdateTemplateInput,
  CreatePresetInput,
  UpdatePresetInput
} from '../types';

import {
  validateCreateProject,
  validateUpdateProject,
  validateCreateDocument,
  validateUpdateDocument,
  validateCreateImage,
  validateCreateTemplate,
  validateContent,
  validateUUID,
  type ValidationResult
} from '../utils/validation';

import { loggers } from './loggerService';

// Create scoped logger for IndexedDB operations
const log = loggers.db;

// ============================================================================
// VALIDATION ERROR CLASS
// ============================================================================

/**
 * Custom error class for validation errors
 */
export class ValidationError extends Error {
  /** Validation result with detailed errors */
  public readonly validation: ValidationResult;

  constructor(message: string, validation: ValidationResult) {
    super(message);
    this.name = 'ValidationError';
    this.validation = validation;
  }
}

/**
 * Error thrown when an IndexedDB write fails due to storage quota being exceeded.
 * Consumers can catch this to show a user-friendly message.
 */
export class StorageQuotaError extends Error {
  /** The store where the write failed */
  public readonly storeName: string;

  constructor(storeName: string, cause?: unknown) {
    super(`Storage quota exceeded while writing to "${storeName}". Free up space by deleting unused projects or images.`);
    this.name = 'StorageQuotaError';
    this.storeName = storeName;
    if (cause) this.cause = cause;
  }
}

/**
 * Helper to throw validation error if validation fails
 */
function assertValid(result: ValidationResult, context: string): void {
  if (!result.valid) {
    const errorMessages = result.errors.map(e => `${e.field}: ${e.message}`).join('; ');
    throw new ValidationError(`${context}: ${errorMessages}`, result);
  }
}

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/** Index definition for object stores */
interface IndexDefinition {
  name: string;
  keyPath: string;
  options: IDBIndexParameters;
}

/** Schema definition for an object store */
interface StoreSchema {
  keyPath: string;
  autoIncrement: boolean;
  indexes: IndexDefinition[];
}

/** Database schema type */
type DatabaseSchema = Record<string, StoreSchema>;

/** Transaction mode */
type TransactionMode = 'readonly' | 'readwrite';

/** Migration statistics */
export interface MigrationStats {
  projects: number;
  documents: number;
  contents: number;
}

/** Migration result */
export interface MigrationResult {
  migrated: boolean;
  stats: MigrationStats;
}

/** Setting record in IndexedDB */
interface SettingRecord {
  key: string;
  value: unknown;
}

/** Content record in IndexedDB */
interface ContentRecord {
  docId: string;
  content: string;
  updatedAt: string;
}

// ============================================================================
// CONFIGURATION
// ============================================================================

const DB_NAME = 'PrepareDB';
const DB_VERSION = 7;  // Added presets store + presetId on templates

/**
 * Database schema - Defines object stores and their indexes
 */
const SCHEMA: DatabaseSchema = {
  projects: {
    keyPath: 'id',
    autoIncrement: false,
    indexes: [
      { name: 'title', keyPath: 'title', options: { unique: false } },
      { name: 'createdAt', keyPath: 'createdAt', options: { unique: false } },
      { name: 'updatedAt', keyPath: 'updatedAt', options: { unique: false } }
    ]
  },
  documents: {
    keyPath: 'id',
    autoIncrement: false,
    indexes: [
      { name: 'projectId', keyPath: 'projectId', options: { unique: false } },
      { name: 'title', keyPath: 'title', options: { unique: false } },
      { name: 'status', keyPath: 'status', options: { unique: false } },
      { name: 'createdAt', keyPath: 'createdAt', options: { unique: false } },
      { name: 'updatedAt', keyPath: 'updatedAt', options: { unique: false } }
    ]
  },
  contents: {
    keyPath: 'docId',
    autoIncrement: false,
    indexes: []
  },
  images: {
    keyPath: 'id',
    autoIncrement: false,
    indexes: [
      { name: 'projectId', keyPath: 'projectId', options: { unique: false } },
      { name: 'filename', keyPath: 'filename', options: { unique: false } },
      { name: 'imageSection', keyPath: 'imageSection', options: { unique: false } },
      { name: 'createdAt', keyPath: 'createdAt', options: { unique: false } }
    ]
  },
  templates: {
    keyPath: 'id',
    autoIncrement: false,
    indexes: [
      { name: 'projectId', keyPath: 'projectId', options: { unique: false } },
      { name: 'presetId', keyPath: 'presetId', options: { unique: false } },
      { name: 'filename', keyPath: 'filename', options: { unique: false } },
      { name: 'templateType', keyPath: 'templateType', options: { unique: false } },
      { name: 'templateSection', keyPath: 'templateSection', options: { unique: false } },
      { name: 'createdAt', keyPath: 'createdAt', options: { unique: false } }
    ]
  },
  presets: {
    keyPath: 'id',
    autoIncrement: false,
    indexes: [
      { name: 'name', keyPath: 'name', options: { unique: false } },
      { name: 'created', keyPath: 'created', options: { unique: false } },
      { name: 'updatedAt', keyPath: 'updatedAt', options: { unique: false } }
    ]
  },
  settings: {
    keyPath: 'key',
    autoIncrement: false,
    indexes: []
  }
};

// ============================================================================
// PROJECTS STORE API
// ============================================================================

interface ProjectsStore {
  getAll(): Promise<Project[]>;
  get(id: string): Promise<Project | undefined>;
  add(project: CreateProjectInput & Partial<Project>): Promise<Project>;
  update(id: string, updates: UpdateProjectInput): Promise<Project>;
  delete(id: string): Promise<void>;
  count(): Promise<number>;
}

// ============================================================================
// DOCUMENTS STORE API
// ============================================================================

interface DocumentsStore {
  getAll(): Promise<Document[]>;
  get(id: string): Promise<Document | undefined>;
  getByProject(projectId: string): Promise<Document[]>;
  add(doc: CreateDocumentInput & Partial<Document>): Promise<Document & { content?: string }>;
  update(id: string, updates: UpdateDocumentInput): Promise<Document>;
  delete(id: string): Promise<void>;
  getByStatus(status: DocumentStatus): Promise<Document[]>;
  count(): Promise<number>;
  countByProject(projectId: string): Promise<number>;
}

// ============================================================================
// CONTENTS STORE API
// ============================================================================

interface ContentsStore {
  get(docId: string): Promise<string>;
  set(docId: string, content: string): Promise<void>;
  delete(docId: string): Promise<void>;
}

// ============================================================================
// SETTINGS STORE API
// ============================================================================

interface SettingsStore {
  get<T>(key: string, defaultValue?: T): Promise<T>;
  set(key: string, value: unknown): Promise<void>;
  delete(key: string): Promise<void>;
  getAll(): Promise<Record<string, unknown>>;
}

// ============================================================================
// IMAGES STORE API
// ============================================================================

interface ImagesStore {
  getAll(): Promise<Image[]>;
  get(id: string): Promise<Image | undefined>;
  getByProject(projectId: string): Promise<Image[]>;
  add(image: CreateImageInput): Promise<Image>;
  update(id: string, updates: UpdateImageInput): Promise<Image>;
  delete(id: string): Promise<void>;
  count(): Promise<number>;
  countByProject(projectId: string): Promise<number>;
}

// ============================================================================
// TEMPLATES STORE API
// ============================================================================

interface TemplatesStore {
  getAll(): Promise<Template[]>;
  get(id: string): Promise<Template | undefined>;
  getByProject(projectId: string): Promise<Template[]>;
  getByPreset(presetId: string): Promise<Template[]>;
  getByType(projectId: string, templateType: string): Promise<Template[]>;
  add(template: CreateTemplateInput): Promise<Template>;
  update(id: string, updates: UpdateTemplateInput): Promise<Template>;
  delete(id: string): Promise<void>;
  count(): Promise<number>;
  countByProject(projectId: string): Promise<number>;
}

// ============================================================================
// PRESETS STORE API
// ============================================================================

interface PresetsStore {
  getAll(): Promise<Preset[]>;
  get(id: string): Promise<Preset | undefined>;
  add(preset: CreatePresetInput): Promise<Preset>;
  update(id: string, updates: UpdatePresetInput): Promise<Preset>;
  delete(id: string): Promise<void>;
  count(): Promise<number>;
}

// ============================================================================
// DATABASE MANAGER
// ============================================================================

// ============================================================================
// BLOB ↔ DATA URL CONVERSION
// ============================================================================

/** Internal IDB record: stores image data as Blob instead of base64 dataUrl. */
interface ImageRecord {
  id: string;
  projectId: string;
  filename: string;
  mimeType: string;
  /** Binary blob (new format, ~33% smaller than base64). */
  blob?: Blob;
  /** Legacy base64 data URL (old format, kept for lazy migration). */
  dataUrl?: string;
  size: number;
  imageSection: ImageSection;
  width?: number;
  height?: number;
  altText?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Convert a data:…;base64,… URL to a Blob.
 */
function dataUrlToBlob(dataUrl: string): Blob {
  const commaIdx = dataUrl.indexOf(',');
  const header = dataUrl.slice(0, commaIdx);
  const base64 = dataUrl.slice(commaIdx + 1);
  const mimeMatch = header.match(/data:([^;]+)/);
  const mime = mimeMatch ? mimeMatch[1] : 'application/octet-stream';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mime });
}

/**
 * Convert a Blob back to a data URL string.
 */
function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Failed to convert Blob to data URL'));
    reader.readAsDataURL(blob);
  });
}

/**
 * Hydrate an ImageRecord from IDB into an Image with dataUrl.
 * Handles both new (blob) and legacy (dataUrl) formats.
 */
async function hydrateImageRecord(record: ImageRecord): Promise<Image> {
  let dataUrl: string;
  if (record.blob) {
    dataUrl = await blobToDataUrl(record.blob);
  } else if (record.dataUrl) {
    // Legacy record — use dataUrl as-is
    dataUrl = record.dataUrl;
  } else {
    dataUrl = '';
  }

  return {
    id: record.id,
    projectId: record.projectId,
    filename: record.filename,
    mimeType: record.mimeType,
    dataUrl,
    size: record.size,
    imageSection: record.imageSection,
    width: record.width,
    height: record.height,
    altText: record.altText,
    createdAt: record.createdAt,
    updatedAt: record.updatedAt,
  };
}

/**
 * Main class for IndexedDB management
 *
 * Singleton service managing all IndexedDB operations for Preparo.
 * Provides high-level APIs for projects, documents, contents, and settings.
 */
class IndexedDBService {
  private db: IDBDatabase | null = null;
  public isInitialized = false;
  private initPromise: Promise<IDBDatabase> | null = null;

  /**
   * Initialize database connection
   *
   * Opens the IndexedDB database and creates object stores if needed.
   * This method is idempotent - multiple calls return the same promise.
   */
  async init(): Promise<IDBDatabase> {
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = new Promise((resolve, reject) => {
      log.info('Initializing IndexedDB');

      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        log.error('IndexedDB initialization failed', { error: request.error });
        reject(new Error(`Failed to open IndexedDB: ${request.error}`));
      };

      request.onsuccess = () => {
        this.db = request.result;
        this.isInitialized = true;
        log.info('IndexedDB initialized successfully', { dbName: DB_NAME, version: DB_VERSION });

        // Handle unexpected closure
        this.db.onversionchange = () => {
          this.db?.close();
          log.warn('Database version changed externally, connection closed');
        };

        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        log.info('Upgrading IndexedDB schema', { version: DB_VERSION });
        const db = (event.target as IDBOpenDBRequest).result;
        const transaction = (event.target as IDBOpenDBRequest).transaction!;

        // Create object stores according to schema
        for (const [storeName, storeConfig] of Object.entries(SCHEMA)) {
          let store: IDBObjectStore;

          if (!db.objectStoreNames.contains(storeName)) {
            store = db.createObjectStore(storeName, {
              keyPath: storeConfig.keyPath,
              autoIncrement: storeConfig.autoIncrement
            });

            // Create indexes for new store
            for (const index of storeConfig.indexes) {
              store.createIndex(index.name, index.keyPath, index.options);
            }

            log.debug('Created object store', { storeName, indexes: storeConfig.indexes.length });
          } else {
            // Store exists, check for missing indexes
            store = transaction.objectStore(storeName);
            for (const index of storeConfig.indexes) {
              if (!store.indexNames.contains(index.name)) {
                store.createIndex(index.name, index.keyPath, index.options);
                log.debug('Added index to existing store', { storeName, indexName: index.name });
              }
            }
          }
        }

        // Migration: Set templateSection for existing templates that don't have it
        if (db.objectStoreNames.contains('templates')) {
          const templatesStore = transaction.objectStore('templates');
          const getAllRequest = templatesStore.getAll();
          getAllRequest.onsuccess = () => {
            const templates = getAllRequest.result as Template[];
            for (const template of templates) {
              if (!template.templateSection) {
                template.templateSection = template.templateType === 'font' ? 'fonts' : 'undefined';
                templatesStore.put(template);
                log.debug('Migrated template with section', { templateId: template.id, section: template.templateSection });
              }
            }
          };
        }

        // Migration: Set imageSection for existing images that don't have it
        if (db.objectStoreNames.contains('images')) {
          const imagesStore = transaction.objectStore('images');
          const getAllImagesRequest = imagesStore.getAll();
          getAllImagesRequest.onsuccess = () => {
            const images = getAllImagesRequest.result as Image[];
            for (const image of images) {
              if (!image.imageSection) {
                // Auto-detect cover images by filename
                const isCover = /^cover\.(jpg|jpeg|png|gif|webp)$/i.test(image.filename);
                image.imageSection = isCover ? 'cover' : 'images';
                imagesStore.put(image);
                log.debug('Migrated image with section', { imageId: image.id, section: image.imageSection });
              }
            }
          };
        }
      };
    });

    return this.initPromise;
  }

  /**
   * Verify that the DB is initialized
   */
  private ensureInitialized(): void {
    if (!this.isInitialized || !this.db) {
      throw new Error('IndexedDB not initialized. Call init() first.');
    }
  }

  /**
   * Execute a transaction
   */
  private async transaction<T>(
    storeNames: string | string[],
    mode: TransactionMode,
    callback: (store: IDBObjectStore) => IDBRequest | Promise<T>
  ): Promise<T> {
    this.ensureInitialized();

    const stores = Array.isArray(storeNames) ? storeNames : [storeNames];

    const storeName = stores[0];

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(stores, mode);
      let result: T;

      transaction.oncomplete = () => resolve(result);
      transaction.onerror = () => reject(this.wrapQuotaError(transaction.error, storeName));
      transaction.onabort = () => reject(this.wrapQuotaError(transaction.error, storeName));

      try {
        const store = transaction.objectStore(storeName);
        const callbackResult = callback(store);

        if (callbackResult instanceof IDBRequest) {
          callbackResult.onsuccess = () => {
            result = callbackResult.result as T;
          };
          callbackResult.onerror = () => reject(this.wrapQuotaError(callbackResult.error as DOMException | null, storeName));
        } else if (callbackResult instanceof Promise) {
          callbackResult.then((r) => {
            result = r;
          }).catch(reject);
        } else {
          result = callbackResult as T;
        }
      } catch (error) {
        transaction.abort();
        reject(error);
      }
    });
  }

  /**
   * Execute a bulk write operation in a single transaction.
   * All items are added/put into the same store within one transaction,
   * which is dramatically faster than one transaction per item.
   */
  private async bulkWrite(
    storeName: string,
    items: unknown[],
    operation: 'add' | 'put'
  ): Promise<void> {
    if (items.length === 0) return;
    this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);

      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(this.wrapQuotaError(tx.error, storeName));
      tx.onabort = () => reject(this.wrapQuotaError(tx.error, storeName));

      for (const item of items) {
        store[operation](item);
      }
    });
  }

  /**
   * Delete multiple keys from a store in a single transaction.
   */
  private async bulkDelete(storeName: string, keys: IDBValidKey[]): Promise<void> {
    if (keys.length === 0) return;
    this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);

      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(this.wrapQuotaError(tx.error, storeName));
      tx.onabort = () => reject(this.wrapQuotaError(tx.error, storeName));

      for (const key of keys) {
        store.delete(key);
      }
    });
  }

  /**
   * Execute an atomic operation across multiple stores in a single transaction.
   * If any operation fails, the entire transaction is rolled back.
   */
  private async atomicTransaction(
    storeNames: string[],
    callback: (stores: Record<string, IDBObjectStore>) => void
  ): Promise<void> {
    this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(storeNames, 'readwrite');

      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(this.wrapQuotaError(tx.error, storeNames.join(',')));
      tx.onabort = () => reject(this.wrapQuotaError(tx.error, storeNames.join(',')));

      try {
        const stores: Record<string, IDBObjectStore> = {};
        for (const name of storeNames) {
          stores[name] = tx.objectStore(name);
        }
        callback(stores);
      } catch (error) {
        tx.abort();
        reject(error);
      }
    });
  }

  /**
   * Wrap a DOMException into a StorageQuotaError if it's a quota issue,
   * otherwise return the original error.
   */
  private wrapQuotaError(error: DOMException | null, storeName: string): Error {
    if (error && (error.name === 'QuotaExceededError' || error.code === 22)) {
      return new StorageQuotaError(storeName, error);
    }
    return error || new Error('Unknown transaction error');
  }

  // ==========================================================================
  // PROJECTS STORE
  // ==========================================================================

  projects: ProjectsStore = {
    getAll: async (): Promise<Project[]> => {
      return this.transaction('projects', 'readonly', (store) => store.getAll());
    },

    get: async (id: string): Promise<Project | undefined> => {
      return this.transaction('projects', 'readonly', (store) => store.get(id));
    },

    add: async (project: CreateProjectInput & Partial<Project>): Promise<Project> => {
      // Validate input
      const validation = validateCreateProject(project);
      assertValid(validation, 'Invalid project data');

      const projectWithMeta: Project = {
        id: project.id || this.generateId(),
        title: project.title.trim(),
        projectType: project.projectType || 'book',
        description: project.description?.trim(),
        author: project.author?.trim(),
        metadata: project.metadata,
        settings: project.settings,
        createdAt: project.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      await this.transaction('projects', 'readwrite', (store) => store.add(projectWithMeta));

      log.info('Project added', { projectId: projectWithMeta.id, title: projectWithMeta.title });
      return projectWithMeta;
    },

    update: async (id: string, updates: UpdateProjectInput): Promise<Project> => {
      // Validate ID
      const idValidation = validateUUID(id, 'id');
      assertValid(idValidation, 'Invalid project ID');

      // Validate updates
      const validation = validateUpdateProject(updates);
      assertValid(validation, 'Invalid project update data');

      const existing = await this.projects.get(id);
      if (!existing) {
        throw new Error(`Project not found: ${id}`);
      }

      const updated: Project = {
        ...existing,
        ...updates,
        // Trim string fields if provided
        title: updates.title?.trim() ?? existing.title,
        description: updates.description?.trim() ?? existing.description,
        author: updates.author?.trim() ?? existing.author,
        id,
        updatedAt: new Date().toISOString()
      };

      await this.transaction('projects', 'readwrite', (store) => store.put(updated));

      log.info('Project updated', { projectId: id });
      return updated;
    },

    delete: async (id: string): Promise<void> => {
      // Gather all child IDs in parallel (3 readonly transactions)
      const [docs, images, templates] = await Promise.all([
        this.documents.getByProject(id),
        this.images.getByProject(id),
        this.templates.getByProject(id),
      ]);

      // Atomic delete: all stores in a single transaction — if anything fails, nothing is deleted
      await this.atomicTransaction(
        ['projects', 'documents', 'contents', 'images', 'templates'],
        (stores) => {
          for (const doc of docs) {
            stores.contents.delete(doc.id);
            stores.documents.delete(doc.id);
          }
          for (const image of images) {
            stores.images.delete(image.id);
          }
          for (const template of templates) {
            stores.templates.delete(template.id);
          }
          stores.projects.delete(id);
        }
      );

      log.info('Project deleted', {
        projectId: id,
        docs: docs.length,
        images: images.length,
        templates: templates.length
      });
    },

    count: async (): Promise<number> => {
      return this.transaction('projects', 'readonly', (store) => store.count());
    }
  };

  // ==========================================================================
  // DOCUMENTS STORE
  // ==========================================================================

  documents: DocumentsStore = {
    getAll: async (): Promise<Document[]> => {
      return this.transaction('documents', 'readonly', (store) => store.getAll());
    },

    get: async (id: string): Promise<Document | undefined> => {
      return this.transaction('documents', 'readonly', (store) => store.get(id));
    },

    getByProject: async (projectId: string): Promise<Document[]> => {
      this.ensureInitialized();
      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction('documents', 'readonly');
        const store = transaction.objectStore('documents');
        const index = store.index('projectId');
        const request = index.getAll(projectId);

        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });
    },

    add: async (doc: CreateDocumentInput & Partial<Document>): Promise<Document & { content?: string }> => {
      // Validate input
      const validation = validateCreateDocument(doc);
      assertValid(validation, 'Invalid document data');

      const { content, ...docWithoutContent } = doc;

      // Validate content if provided
      if (content !== undefined) {
        const contentValidation = validateContent(content);
        assertValid(contentValidation, 'Invalid document content');
      }

      const docWithMeta: Document = {
        id: docWithoutContent.id || this.generateId(),
        projectId: docWithoutContent.projectId,
        title: docWithoutContent.title.trim(),
        filename: docWithoutContent.filename,
        status: docWithoutContent.status || 'todo',
        documentType: docWithoutContent.documentType,
        documentSection: docWithoutContent.documentSection,
        order: docWithoutContent.order,
        wordCount: docWithoutContent.wordCount,
        charCount: docWithoutContent.charCount,
        createdAt: docWithoutContent.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      await this.transaction('documents', 'readwrite', (store) => store.add(docWithMeta));

      // Save content separately if present
      if (content !== undefined) {
        await this.contents.set(docWithMeta.id, content);
      }

      log.info('Document added', { docId: docWithMeta.id, projectId: docWithMeta.projectId, title: docWithMeta.title });
      return { ...docWithMeta, content };
    },

    update: async (id: string, updates: UpdateDocumentInput): Promise<Document> => {
      // Validate ID
      const idValidation = validateUUID(id, 'id');
      assertValid(idValidation, 'Invalid document ID');

      // Validate updates
      const validation = validateUpdateDocument(updates);
      assertValid(validation, 'Invalid document update data');

      const existing = await this.documents.get(id);
      if (!existing) {
        throw new Error(`Document not found: ${id}`);
      }

      const { content, ...updatesWithoutContent } = updates;

      // Validate content if provided
      if (content !== undefined) {
        const contentValidation = validateContent(content);
        assertValid(contentValidation, 'Invalid document content');
      }

      const updated: Document = {
        ...existing,
        ...updatesWithoutContent,
        // Trim title if provided
        title: updates.title?.trim() ?? existing.title,
        id,
        updatedAt: new Date().toISOString()
      };

      await this.transaction('documents', 'readwrite', (store) => store.put(updated));

      // Update content if provided
      if (content !== undefined) {
        await this.contents.set(id, content);
      }

      log.debug('Document updated', { docId: id });
      return updated;
    },

    delete: async (id: string): Promise<void> => {
      // Atomic delete: document + its content in one transaction
      await this.atomicTransaction(['documents', 'contents'], (stores) => {
        stores.documents.delete(id);
        stores.contents.delete(id);
      });

      log.info('Document deleted', { docId: id });
    },

    getByStatus: async (status: DocumentStatus): Promise<Document[]> => {
      this.ensureInitialized();
      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction('documents', 'readonly');
        const store = transaction.objectStore('documents');
        const index = store.index('status');
        const request = index.getAll(status);

        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });
    },

    count: async (): Promise<number> => {
      return this.transaction('documents', 'readonly', (store) => store.count());
    },

    countByProject: async (projectId: string): Promise<number> => {
      this.ensureInitialized();
      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction('documents', 'readonly');
        const store = transaction.objectStore('documents');
        const index = store.index('projectId');
        const request = index.count(projectId);

        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });
    }
  };

  // ==========================================================================
  // CONTENTS STORE
  // ==========================================================================

  contents: ContentsStore = {
    get: async (docId: string): Promise<string> => {
      const result = await this.transaction<ContentRecord | undefined>(
        'contents',
        'readonly',
        (store) => store.get(docId)
      );
      return result?.content || '';
    },

    set: async (docId: string, content: string): Promise<void> => {
      const record: ContentRecord = {
        docId,
        content,
        updatedAt: new Date().toISOString()
      };
      await this.transaction('contents', 'readwrite', (store) => store.put(record));
      log.debug('Content saved', { docId, contentLength: content.length });
    },

    delete: async (docId: string): Promise<void> => {
      await this.transaction('contents', 'readwrite', (store) => store.delete(docId));
    }
  };

  // ==========================================================================
  // SETTINGS STORE
  // ==========================================================================

  settings: SettingsStore = {
    get: async <T>(key: string, defaultValue: T = null as T): Promise<T> => {
      const result = await this.transaction<SettingRecord | undefined>(
        'settings',
        'readonly',
        (store) => store.get(key)
      );
      return (result?.value as T) ?? defaultValue;
    },

    set: async (key: string, value: unknown): Promise<void> => {
      await this.transaction('settings', 'readwrite', (store) =>
        store.put({ key, value })
      );
    },

    delete: async (key: string): Promise<void> => {
      await this.transaction('settings', 'readwrite', (store) => store.delete(key));
    },

    getAll: async (): Promise<Record<string, unknown>> => {
      const results = await this.transaction<SettingRecord[]>(
        'settings',
        'readonly',
        (store) => store.getAll()
      );
      const settings: Record<string, unknown> = {};
      for (const item of results) {
        settings[item.key] = item.value;
      }
      return settings;
    }
  };

  // ==========================================================================
  // IMAGES STORE
  // ==========================================================================

  images: ImagesStore = {
    getAll: async (): Promise<Image[]> => {
      const records = await this.transaction<ImageRecord[]>('images', 'readonly', (store) => store.getAll());
      return Promise.all(records.map(hydrateImageRecord));
    },

    get: async (id: string): Promise<Image | undefined> => {
      const record = await this.transaction<ImageRecord | undefined>('images', 'readonly', (store) => store.get(id));
      return record ? hydrateImageRecord(record) : undefined;
    },

    getByProject: async (projectId: string): Promise<Image[]> => {
      const records = await this.transaction<ImageRecord[]>('images', 'readonly', (store) => {
        const index = store.index('projectId');
        return index.getAll(projectId);
      });
      return Promise.all(records.map(hydrateImageRecord));
    },

    add: async (input: CreateImageInput): Promise<Image> => {
      // Validate input
      const validation = validateCreateImage(input);
      assertValid(validation, 'Invalid image data');

      // Determine imageSection: auto-detect cover images by filename
      let imageSection = input.imageSection;
      if (!imageSection) {
        const isCover = /^cover\.(jpg|jpeg|png|gif|webp)$/i.test(input.filename);
        imageSection = isCover ? 'cover' : 'images';
      }

      const now = new Date().toISOString();
      const id = this.generateId();

      // Store as Blob (~33% smaller than base64 dataUrl string)
      const record: ImageRecord = {
        id,
        projectId: input.projectId,
        filename: input.filename.trim(),
        mimeType: input.mimeType,
        blob: dataUrlToBlob(input.dataUrl),
        size: input.size,
        imageSection,
        width: input.width,
        height: input.height,
        altText: input.altText?.trim(),
        createdAt: now,
        updatedAt: now,
      };

      await this.transaction('images', 'readwrite', (store) => store.add(record));
      log.info('Image added', { imageId: id, filename: record.filename, size: record.size, section: record.imageSection });

      // Return with dataUrl for immediate consumer use
      return { ...record, dataUrl: input.dataUrl } as Image;
    },

    update: async (id: string, updates: UpdateImageInput): Promise<Image> => {
      const existingRecord = await this.transaction<ImageRecord | undefined>('images', 'readonly', (store) => store.get(id));
      if (!existingRecord) {
        throw new Error(`Image not found: ${id}`);
      }

      // If dataUrl is being updated, convert to Blob for storage
      const blobUpdate = updates.dataUrl ? { blob: dataUrlToBlob(updates.dataUrl) } : {};
      const { dataUrl: _discard, ...updatesWithoutDataUrl } = updates;

      const updatedRecord: ImageRecord = {
        ...existingRecord,
        ...updatesWithoutDataUrl,
        ...blobUpdate,
        updatedAt: new Date().toISOString()
      };
      // Remove legacy dataUrl from stored record if we now have a blob
      if (updatedRecord.blob) {
        delete updatedRecord.dataUrl;
      }

      await this.transaction('images', 'readwrite', (store) => store.put(updatedRecord));
      return hydrateImageRecord(updatedRecord);
    },

    delete: async (id: string): Promise<void> => {
      await this.transaction('images', 'readwrite', (store) => store.delete(id));
      log.info('Image deleted', { imageId: id });
    },

    count: async (): Promise<number> => {
      return this.transaction<number>('images', 'readonly', (store) => store.count());
    },

    countByProject: async (projectId: string): Promise<number> => {
      const images = await this.images.getByProject(projectId);
      return images.length;
    }
  };

  // ==========================================================================
  // TEMPLATES STORE
  // ==========================================================================

  templates: TemplatesStore = {
    getAll: async (): Promise<Template[]> => {
      return this.transaction<Template[]>('templates', 'readonly', (store) => store.getAll());
    },

    get: async (id: string): Promise<Template | undefined> => {
      return this.transaction<Template | undefined>('templates', 'readonly', (store) => store.get(id));
    },

    getByProject: async (projectId: string): Promise<Template[]> => {
      return this.transaction<Template[]>('templates', 'readonly', (store) => {
        const index = store.index('projectId');
        return index.getAll(projectId);
      });
    },

    getByPreset: async (presetId: string): Promise<Template[]> => {
      return this.transaction<Template[]>('templates', 'readonly', (store) => {
        const index = store.index('presetId');
        return index.getAll(presetId);
      });
    },

    getByType: async (projectId: string, templateType: string): Promise<Template[]> => {
      const allTemplates = await this.templates.getByProject(projectId);
      return allTemplates.filter(t => t.templateType === templateType);
    },

    add: async (input: CreateTemplateInput): Promise<Template> => {
      // Validate input
      const validation = validateCreateTemplate(input);
      assertValid(validation, 'Invalid template data');

      // Determine templateSection based on type
      let templateSection = input.templateSection;
      if (!templateSection) {
        if (input.templateType === 'font') {
          templateSection = 'fonts';
        } else if (input.templateType === 'preparo') {
          templateSection = 'preparo';
        } else if (input.templateType === 'lua') {
          templateSection = 'luafilters';
        } else if (input.templateType === 'csl') {
          templateSection = 'csl';
        } else if (input.templateType === 'indesign') {
          templateSection = 'indesign';
        } else if (input.templateType === 'config') {
          templateSection = 'config';
        } else {
          templateSection = 'undefined';
        }
      }

      const now = new Date().toISOString();
      const template: Template = {
        id: this.generateId(),
        ...input,
        templateSection,
        filename: input.filename.trim(),
        documentType: input.documentType || null,
        description: input.description?.trim(),
        createdAt: now,
        updatedAt: now
      };

      await this.transaction('templates', 'readwrite', (store) => store.add(template));
      log.info('Template added', { templateId: template.id, filename: template.filename, type: template.templateType, section: template.templateSection });
      return template;
    },

    update: async (id: string, updates: UpdateTemplateInput): Promise<Template> => {
      const existing = await this.templates.get(id);
      if (!existing) {
        throw new Error(`Template not found: ${id}`);
      }

      const updated: Template = {
        ...existing,
        ...updates,
        updatedAt: new Date().toISOString()
      };

      await this.transaction('templates', 'readwrite', (store) => store.put(updated));
      log.debug('Template updated', { templateId: id });
      return updated;
    },

    delete: async (id: string): Promise<void> => {
      await this.transaction('templates', 'readwrite', (store) => store.delete(id));
      log.info('Template deleted', { templateId: id });
    },

    count: async (): Promise<number> => {
      return this.transaction<number>('templates', 'readonly', (store) => store.count());
    },

    countByProject: async (projectId: string): Promise<number> => {
      const templates = await this.templates.getByProject(projectId);
      return templates.length;
    }
  };

  // ==========================================================================
  // PRESETS STORE
  // ==========================================================================

  presets: PresetsStore = {
    getAll: async (): Promise<Preset[]> => {
      return this.transaction<Preset[]>('presets', 'readonly', (store) => store.getAll());
    },

    get: async (id: string): Promise<Preset | undefined> => {
      return this.transaction<Preset | undefined>('presets', 'readonly', (store) => store.get(id));
    },

    add: async (input: CreatePresetInput): Promise<Preset> => {
      const now = new Date().toISOString();
      const preset: Preset = {
        id: this.generateId(),
        name: input.name.trim(),
        created: now,
        updatedAt: now,
        mapping: input.mapping,
        typography: input.typography,
        pagelayout: input.pagelayout,
        grep: input.grep,
        export: input.export,
        general: input.general,
        metadata: input.metadata
      };

      await this.transaction('presets', 'readwrite', (store) => store.add(preset));
      log.info('Preset added', { presetId: preset.id, name: preset.name });
      return preset;
    },

    update: async (id: string, updates: UpdatePresetInput): Promise<Preset> => {
      const existing = await this.presets.get(id);
      if (!existing) {
        throw new Error(`Preset not found: ${id}`);
      }

      const updated: Preset = {
        ...existing,
        ...updates,
        updatedAt: new Date().toISOString()
      };

      await this.transaction('presets', 'readwrite', (store) => store.put(updated));
      log.debug('Preset updated', { presetId: id });
      return updated;
    },

    delete: async (id: string): Promise<void> => {
      // Gather templates first (readonly), then atomic delete preset + templates
      const presetTemplates = await this.templates.getByPreset(id);

      await this.atomicTransaction(['presets', 'templates'], (stores) => {
        for (const tpl of presetTemplates) {
          stores.templates.delete(tpl.id);
        }
        stores.presets.delete(id);
      });

      log.info('Preset deleted', { presetId: id, templatesDeleted: presetTemplates.length });
    },

    count: async (): Promise<number> => {
      return this.transaction<number>('presets', 'readonly', (store) => store.count());
    }
  };

  // ==========================================================================
  // UTILITIES
  // ==========================================================================

  /**
   * Generate a unique ID using crypto.randomUUID()
   */
  generateId(): string {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    // Fallback for older environments
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  /**
   * Detect and remove orphaned records left by incomplete cascading deletes.
   * Safe to call at startup — only deletes records whose parent no longer exists.
   * Returns counts of removed orphans for diagnostics.
   */
  async cleanOrphans(): Promise<{ contents: number; documents: number; images: number; templates: number }> {
    this.ensureInitialized();

    // Read all parent IDs in parallel
    const [projectIds, documentIds] = await Promise.all([
      this.projects.getAll().then(ps => new Set(ps.map(p => p.id))),
      this.documents.getAll().then(ds => new Set(ds.map(d => d.id))),
    ]);

    // Read all children in parallel
    const [allDocuments, allContents, allImages, allTemplates] = await Promise.all([
      this.documents.getAll(),
      this.transaction<ContentRecord[]>('contents', 'readonly', (store) => store.getAll()),
      this.images.getAll(),
      this.templates.getAll(),
    ]);

    // Find orphans
    const orphanDocs = allDocuments.filter(d => !projectIds.has(d.projectId));
    const orphanContents = allContents.filter(c => !documentIds.has(c.docId));
    const orphanImages = allImages.filter(i => !projectIds.has(i.projectId));
    const orphanTemplates = allTemplates.filter(t => t.projectId && !projectIds.has(t.projectId));

    const total = orphanDocs.length + orphanContents.length + orphanImages.length + orphanTemplates.length;
    if (total === 0) {
      return { contents: 0, documents: 0, images: 0, templates: 0 };
    }

    // Remove all orphans in one atomic transaction
    await this.atomicTransaction(
      ['documents', 'contents', 'images', 'templates'],
      (stores) => {
        for (const doc of orphanDocs) stores.documents.delete(doc.id);
        for (const content of orphanContents) stores.contents.delete(content.docId);
        for (const image of orphanImages) stores.images.delete(image.id);
        for (const template of orphanTemplates) stores.templates.delete(template.id);
      }
    );

    const stats = {
      contents: orphanContents.length,
      documents: orphanDocs.length,
      images: orphanImages.length,
      templates: orphanTemplates.length,
    };

    log.warn('Orphaned records cleaned', stats);
    return stats;
  }

  /**
   * Completely clear all data from the database
   */
  async clear(): Promise<void> {
    this.ensureInitialized();
    const storeNames = Object.keys(SCHEMA);

    // Clear all stores in a single multi-store transaction
    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(storeNames, 'readwrite');
      tx.oncomplete = () => {
        log.info('Database cleared');
        resolve();
      };
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(new Error('Database clear aborted'));

      for (const name of storeNames) {
        tx.objectStore(name).clear();
      }
    });
  }

  /**
   * Export all data for backup or migration
   */
  async exportAll(): Promise<ExportData> {
    const contents = await this.transaction<ContentRecord[]>(
      'contents',
      'readonly',
      (store) => store.getAll()
    );

    return {
      projects: await this.projects.getAll(),
      documents: await this.documents.getAll(),
      contents: contents.map((c) => ({
        docId: c.docId,
        content: c.content,
        updatedAt: c.updatedAt
      })),
      images: await this.images.getAll(),
      templates: await this.templates.getAll(),
      presets: await this.presets.getAll(),
      settings: await this.settings.getAll(),
      exportedAt: new Date().toISOString(),
      version: DB_VERSION
    };
  }

  /**
   * Import data from a backup
   */
  async importAll(data: ExportData): Promise<void> {
    log.info('Starting data import');

    // Clear existing database
    await this.clear();

    // Bulk import — one transaction per store instead of one per item
    await this.bulkWrite('projects', data.projects || [], 'add');
    await this.bulkWrite('documents', data.documents || [], 'add');
    await this.bulkWrite('contents', data.contents || [], 'add');
    await this.bulkWrite('images', data.images || [], 'add');
    await this.bulkWrite('templates', data.templates || [], 'add');
    await this.bulkWrite('presets', data.presets || [], 'add');

    // Settings use a key/value format — still bulk-writable
    const settingsRecords = Object.entries(data.settings || {}).map(
      ([key, value]) => ({ key, value })
    );
    await this.bulkWrite('settings', settingsRecords, 'add');

    log.info('Data import completed successfully', {
      projects: data.projects?.length || 0,
      documents: data.documents?.length || 0,
      images: data.images?.length || 0,
      templates: data.templates?.length || 0,
      presets: data.presets?.length || 0
    });
  }

  /**
   * Get database statistics
   */
  async getStats(): Promise<DatabaseStats> {
    const contentsCount = await this.transaction<number>(
      'contents',
      'readonly',
      (store) => store.count()
    );

    return {
      projects: await this.projects.count(),
      documents: await this.documents.count(),
      contents: contentsCount,
      images: await this.images.count(),
      templates: await this.templates.count(),
      presets: await this.presets.count(),
      settings: Object.keys(await this.settings.getAll()).length
    };
  }

  /**
   * Close the database connection
   */
  close(): void {
    if (this.db) {
      this.db.close();
      this.db = null;
      this.isInitialized = false;
      this.initPromise = null;
      log.info('IndexedDB connection closed');
    }
  }

  /**
   * Delete the entire database
   */
  async deleteDatabase(): Promise<void> {
    this.close();
    return new Promise((resolve, reject) => {
      const request = indexedDB.deleteDatabase(DB_NAME);
      request.onsuccess = () => {
        log.warn('Database deleted', { dbName: DB_NAME });
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  }
}

// ============================================================================
// MIGRATION FROM LOCALSTORAGE
// ============================================================================

/**
 * Migrate data from localStorage to IndexedDB
 */
export async function migrateFromLocalStorage(dbInstance: IndexedDBService): Promise<MigrationResult> {
  log.info('Checking for localStorage data to migrate');

  const stats: MigrationStats = {
    projects: 0,
    documents: 0,
    contents: 0
  };

  try {
    // Check if there is localStorage data
    const projectsKey = 'preparo_projects';
    const storedProjects = localStorage.getItem(projectsKey);

    if (!storedProjects) {
      log.debug('No localStorage data found, skipping migration');
      return { migrated: false, stats };
    }

    // Check if migration has already been done
    const migrationDone = await dbInstance.settings.get<boolean>('localStorageMigrationDone');
    if (migrationDone) {
      log.debug('Migration already completed');
      return { migrated: false, stats };
    }

    log.info('Migrating data from localStorage to IndexedDB');

    // Migrate projects
    const projects = JSON.parse(storedProjects) as Array<{ id: string; [key: string]: unknown }>;
    for (const project of projects) {
      await dbInstance.projects.add(project as unknown as CreateProjectInput & Partial<Project>);
      stats.projects++;

      // Migrate documents for this project
      const docsKey = `preparo_docs_${project.id}`;
      const storedDocs = localStorage.getItem(docsKey);

      if (storedDocs) {
        const docs = JSON.parse(storedDocs) as Array<{ id: string; [key: string]: unknown }>;
        for (const doc of docs) {
          // Get document content
          const contentKey = `preparo_doc_${doc.id}`;
          const content = localStorage.getItem(contentKey) || '';

          await dbInstance.documents.add({
            ...doc,
            projectId: project.id,
            title: (doc.title as string) || 'Untitled',
            content
          } as CreateDocumentInput);
          stats.documents++;

          if (content) {
            stats.contents++;
          }
        }
      }
    }

    // Mark migration as done
    await dbInstance.settings.set('localStorageMigrationDone', true);
    await dbInstance.settings.set('localStorageMigrationDate', new Date().toISOString());

    log.info('Migration completed', { ...stats });

    return { migrated: true, stats };
  } catch (error) {
    log.error('Migration failed', { error });
    throw error;
  }
}

// ============================================================================
// SINGLETON INSTANCE
// ============================================================================

export const db = new IndexedDBService();

export default db;
